import { create } from 'zustand';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  dealership_id?: string;
}

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  setAuth: (token: string, user: User, remember?: boolean) => void;
  logout: () => void;
}

// Initialize token from storage on store creation
const getStoredToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};

const getStoredUser = () => {
  const token = getStoredToken();
  if (!token) return null;

  try {
    // Parse JWT to get user info (payload is the second part of the JWT)
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload);

    // Check if token is expired
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      return null;
    }

    return {
      id: payload.sub || payload.user_id || '',
      email: payload.email || '',
      name: payload.name || payload.username || '',
      role: payload.role || 'agent',
      dealership_id: payload.dealership_id || undefined,
    };
  } catch (e) {
    console.error('Error parsing stored token:', e);
    return null;
  }
};

const initialToken = getStoredToken();
const initialUser = getStoredUser();

export const useAuthStore = create<AuthState>((set) => ({
  token: initialToken,
  user: initialUser,
  isAuthenticated: !!initialToken && !!initialUser,
  setAuth: (token, user, remember = false) => {
    if (remember) {
      localStorage.setItem('token', token);
      sessionStorage.removeItem('token'); // Clear session storage if remember is true
    } else {
      sessionStorage.setItem('token', token);
      localStorage.removeItem('token'); // Clear local storage if remember is false
    }
    set({ token, user, isAuthenticated: true });
  },
  logout: () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    set({ token: null, user: null, isAuthenticated: false });
  },
}));